/* eslint-disable @reatom/unit-naming-rule */
import { action, onConnect, reaction } from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'
import { isLoggedInAtom } from '@root/entities/auth'
import { Network } from '@root/entities/network'
import { pairRegistry } from '@root/entities/pair/atoms/pair-registry'
import {
  fetchActivePositionRequestAction,
  reatomWalletPosition,
} from '@root/entities/position'
import { userPositionUpdateEvent } from '@root/entities/realtime'
import { reatomRequest } from '@root/shared/lib'

import type { PairAddress } from '@root/entities/pair/model/PairAddress'
import type { WalletId } from '@root/entities/wallet'

export const reatomActivePosition = ({
  pairAddress,
  network,
  walletId,
}: {
  network: Network.Network
  walletId: WalletId
  pairAddress: PairAddress
}) => {
  const execInternal = action(async (ctx) => {
    const result = await ctx.schedule(() =>
      fetchActivePositionRequestAction(ctx, {
        pairAddress,
        walletId,
        network,
      }),
    )

    return result.pipe(
      Result.map((it) =>
        it.position ? reatomWalletPosition.create(ctx, it.position) : undefined,
      ),
    )
  }, 'execInternal')

  const activePositionStateAtom = reatomRequest({
    execAction: execInternal,
    name: 'reatomActivePosition',
  })

  const loadReaction = reaction(async (ctx) => {
    const isLoggedIn = ctx.spy(isLoggedInAtom)
    if (!isLoggedIn) {
      activePositionStateAtom.setState(ctx, AsyncStateN.success(undefined))
      return
    }

    activePositionStateAtom.execute(ctx)
  }, 'loadReaction')

  onConnect(activePositionStateAtom, (ctx) => {
    const sub = loadReaction(ctx)
    const unsubscribePositionUpdate = userPositionUpdateEvent.onCall(
      (ctx, walletPosition) => {
        const state = ctx.get(activePositionStateAtom)
        // Check if the position is new
        if (AsyncStateN.isSuccess(state) && state.value) {
          return
        }
        const pair = pairRegistry.get(pairAddress, network)
        if (!pair) {
          return
        }

        if (
          walletPosition.pair === pairAddress &&
          Network.fromDto(walletPosition.network) === network &&
          walletPosition.walletId === walletId
        ) {
          activePositionStateAtom.setState(
            ctx,
            AsyncStateN.success(
              reatomWalletPosition({
                dto: walletPosition,
                pair,
              }),
            ),
          )
        }
      },
    )

    return () => {
      sub.unsubscribe()
      unsubscribePositionUpdate()
    }
  })

  return activePositionStateAtom
}
